// 前任者がメーカー専用に足した「見た目の決まり」を、新しい作る画面でも掛ける（2026-10-27〜、GENESIS2 から）。
// クラシック版は charat.legacy.js の同じ処理（skinColorLink・updateStopColorInSVG・fupdateLineColorAndPathForMultipleIds）を
// パーツ・色を替えたあとに掛けている。同じ保存データが同じ絵になるように、色の計算・選ぶ要素・順番はそれと同じにする。
// メーカーの設定（$ui['js']['look']）に書いたものだけ掛ける（無いメーカーは何もしない）:
//   skinBase   肌の色の元になるレイヤー（クラシック版のページの baseskin '#lcart_body' の body）。その色から .skincolor〜.skincolor5 の塗り・.skincolorl の線を決める
//   gradStops  グラデーションの止めの色をパーツの色にするレイヤー（クラシック版は前髪 bangs だけ。前髪の透けのグラデーション）
//   lineColor  線の色をパーツの色から作るレイヤー（クラシック版のページの linecolorids。.skincolorl の中は除く）
//   blurFilters パーツが使うぼかしの filter（#filblur1〜10）と乗算の filter をキャラの SVG に置く（クラシック版は addFilterblur。ほほ・ひざの赤みなど。
//               パーツの SVG のファイルにも定義はあるが、パーツは直下の要素ごとに切り分けるので入らない）
//   adjustColors リボン（bow）・インナー（inner）・スカートの裏（skirtinner）のカテゴリの色を、ほかのパーツの .bowcolor・.innercolor・.skirtcolor に（クラシック版は adjustSVGColors。DRESSUP2）
//   eyeshadow   アイシャドウのカテゴリのパーツの番号で .eyeshadow1〜4 を出し分ける（updateEyeshadowVisibility。DRESSUP2）
//   lipOpacity  くちのうすさを .lip に（updateLipOpacity。DRESSUP2）
//   outline     キャラ全体の複製に黒い線を付けて背景（bg2_f）の直後に敷く＝キャラの縁の黒い線（copyAndOutlineElements、ページの outlinemode。DRESSUP2）。
//               クラシック版はパーツを替えたあと・移動・拡大・回転のあとに作り直す（色を替えたあとは作り直さない）
//   colorFilter 色のフィルター（filter のカテゴリの番号で、キャラ全体にセピア・モノクロなどの色の行列。filterCheck。KAWAII2）
//   excHands    手の見え方（両手・左手・右手のどれかを出す）と袖（cloth_y）（updateHandVisibility。KAWAII）
// 掛けるのは 1 カテゴリを描き直したあと（drawn）と、全部を描き直したあと（drawnAll）。全部のときは最後に 1 回だけ
import { C } from './core/model.js';
import { on } from './core/events.js';
import { CONFIG } from './config.js';
import { keyOf } from './core/util.js';
import { findPart, layersOf } from './core/data.js';
import { place } from './core/render.js';
import type { Category, Part } from './types.js';
import { pairThumbSide, sideKeys } from './merge.js';

const LOOK = CONFIG.look || null;
const SVGNS = 'http://www.w3.org/2000/svg';

// レイヤー名 → そのレイヤーのあるカテゴリ（クラシック版はレイヤーの g の paramCol を読む。中身は同じカテゴリの色を全レイヤーに書く）
let layerCat: Record<string, Category> | null = null;
function catOfLayer(name: string): Category | null {
  if (!layerCat) {
    layerCat = Object.create(null) as Record<string, Category>;
    C.cats.forEach((cat) => cat.LAYER.forEach((l) => { if (!(l in layerCat!)) layerCat![l] = cat; }));
  }
  return layerCat[name] || null;
}
// クラシック版のレイヤーの属性（中身の core/classic.ts の mirror と同じ）: パーツ番号・色はカテゴリの先頭レイヤーにだけ（パーツがあるとき）、
// ほかのレイヤーは空。うすさは全レイヤー（パーツが無ければ 1）。クラシック版の後処理はこの属性を読む
function param(name: string, attr: 'paramParts' | 'paramCol' | 'paramOpacity'): string {
  const cat = catOfLayer(name);
  if (!cat) return attr === 'paramOpacity' ? '1' : '';
  const st = C.state[keyOf(cat)];
  const drawn = !!(st && st.parts != null && findPart(cat, st.parts));
  const head = cat.LAYER[0] === name && drawn;
  if (attr === 'paramOpacity') return String(drawn && st.opacity !== '' && st.opacity != null ? st.opacity : 1);
  if (attr === 'paramParts') return head ? String(st.parts) : '';
  return head ? String(st.col || '') : '';
}
// レイヤーの色（クラシック版の paramCol。無ければ null）。_f・_y のレイヤーに無ければ _r を見るのもクラシック版と同じ
function layerColor(name: string): string | null {
  return param(name, 'paramCol') || (/_[fy]$/.test(name) ? param(name.replace(/_[fy]$/, '_r'), 'paramCol') : '') || null;
}
function layerNode(name: string): Element | null {
  return C.svg ? C.svg.querySelector('#lcart_' + CSS.escape(name)) : null;
}

// d3.rgb と同じ読み方・書き方（クラシック版は d3 の色で計算して rgb(r, g, b) の文字で塗る。0〜255 に丸める）
function rgbOf(c: string): [number, number, number] | null {
  const s = c.trim();
  let m = /^#([0-9a-f]{3})$/i.exec(s);
  if (m) return [0, 1, 2].map((i) => parseInt(m![1][i] + m![1][i], 16)) as [number, number, number];
  m = /^#([0-9a-f]{6})$/i.exec(s);
  if (m) return [0, 2, 4].map((i) => parseInt(m![1].slice(i, i + 2), 16)) as [number, number, number];
  m = /^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)/i.exec(s);
  if (m) return [Number(m[1]), Number(m[2]), Number(m[3])];
  return null;
}
const ch = (v: number) => Math.max(0, Math.min(255, Math.round(v) || 0));
const rgbText = (r: number, g: number, b: number) => 'rgb(' + ch(r) + ', ' + ch(g) + ', ' + ch(b) + ')';

// 肌の色の連動（charat.legacy.js の skinColorLink と同じ式）
function skinLink(base: string) {
  if (!C.svg) return;
  const skin = layerColor(base) || '#FFFFFF';
  const c = rgbOf(skin);
  if (!c) return;
  const [r, g, b] = c;
  const colors: [string, string, string][] = [
    ['.skincolor', 'fill', skin],
    ['.skincolor2', 'fill', rgbText(Math.max(0, r - 20), Math.max(0, g - 30), Math.max(0, b - 30))],
    ['.skincolor3', 'fill', rgbText(Math.max(0, r - 2), Math.max(0, g - 18), Math.max(0, b - 19))],
    ['.skincolor4', 'fill', rgbText(Math.max(0, r * 0.98), Math.max(0, g * 0.9), Math.max(0, b * 0.9))],
    ['.skincolor5', 'fill', rgbText(Math.max(0, r * 0.8), Math.max(0, g * 0.65), Math.max(0, b * 0.68))],
    ['.skincolorl', 'stroke', rgbText(Math.max(0, r * 0.65), Math.max(0, g * 0.51), Math.max(0, b * 0.58))],
  ];
  colors.forEach(([sel, prop, value]) => {
    C.svg.querySelectorAll(sel).forEach((node) => {
      (node as SVGElement).style.setProperty(prop, value);
      node.querySelectorAll('*').forEach((d) => (d as SVGElement).style.setProperty(prop, value));
    });
  });
}

// グラデーションの止めの色をパーツの色に（charat.legacy.js の updateStopColorInSVG と同じ。style の stop-color だけ書きかえる）
function gradStops(name: string) {
  const g = layerNode(name), col = layerColor(name);
  if (!g || !col) return;
  g.querySelectorAll('linearGradient stop').forEach((stop) => {
    const style = stop.getAttribute('style');
    if (style) stop.setAttribute('style', style.replace(/stop-color:[^;]+/, 'stop-color:' + col));
  });
}

// 線の色をパーツの色から（charat.legacy.js の fupdateLineColorAndPathForMultipleIds と同じ式・同じ要素）。
// 先に全部の要素の線の有無を読んでから書く（1 つずつ書くと、そのたびにスタイルを計算し直すので遅い。図形の中に図形は無いので、読む順で結果は変わらない）
const SHAPES = 'line, path, polyline, polygon, rect, circle, ellipse';
function lineColor(names: string[]) {
  const writes: [SVGElement, string][] = [];
  names.forEach((name) => {
    const g = layerNode(name), col = layerColor(name);
    if (!g || !col) return;
    const c = rgbOf(col);
    if (!c) return;
    const color = rgbText(c[0] * 0.65, c[1] * 0.51, c[2] * 0.58);
    g.querySelectorAll(SHAPES).forEach((node) => {
      // .skincolorl の要素とその中は除く（肌の線は肌の色から）
      for (let p: Element | null = node; p && p.tagName !== 'svg'; p = p.parentElement) if (p.classList.contains('skincolorl')) return;
      const el = node as SVGElement;
      const stroke = el.style.getPropertyValue('stroke') || getComputedStyle(el).getPropertyValue('stroke');
      if (stroke !== 'none') writes.push([el, color]);
    });
  });
  writes.forEach(([el, color]) => el.style.setProperty('stroke', color));
}

// ---- DRESSUP2（2026-10-27〜） ----
const hexMinus = (c: string, d: number): string | null => {
  const m = c.substring(1).match(/.{1,2}/g);
  if (!m) return null;
  return '#' + m.map((h) => Math.max(parseInt(h, 16) - d, 0)).map((v) => v.toString(16).padStart(2, '0')).join('');
};
const fillAll = (node: Element, color: string) => node.querySelectorAll('*').forEach((c) => { (c as SVGElement).style.fill = color; });
// リボン・インナー・スカートの裏（charat.legacy.js の adjustSVGColors と同じ。カテゴリのレイヤーそのものは隠し、ほかのパーツの印の所に色を）
function adjustColors() {
  const q = (sel: string) => C.svg.querySelectorAll(sel);
  const bow = layerNode('bow'), inner = layerNode('inner'), skirtinner = layerNode('skirtinner');
  if (bow) (bow as SVGElement).style.display = 'none';
  if (inner) (inner as SVGElement).style.display = 'none';
  if (skirtinner) (skirtinner as SVGElement).style.display = 'none';
  if (bow) {
    const col = param('bow', 'paramCol');
    if (param('bow', 'paramParts') !== '1') {
      q('g[id="lcart_cloth_f"] .bowtie').forEach((n) => { (n as SVGElement).style.display = ''; });
      q('g[id="lcart_cloth_f"] .bowcolor').forEach((n) => {
        const tag = n.tagName.toLowerCase();
        if (tag === 'path' || tag === 'g') (n as SVGElement).style.fill = col;
        fillAll(n, col);
      });
      q('g[id="lcart_cloth_f"] .bowcolor2').forEach((n) => {
        const c2 = hexMinus(col, 30);
        if (!c2) return;
        if (n.tagName.toLowerCase() === 'path') (n as SVGElement).style.fill = c2;
        fillAll(n, c2);
      });
    } else q('g[id="lcart_cloth_f"] .bowtie').forEach((n) => { (n as SVGElement).style.display = 'none'; });
  }
  if (inner) {
    const one = param('inner', 'paramParts') === '1', col = param('inner', 'paramCol');
    q('g.innercolor').forEach((n) => {
      (n as SVGElement).style.opacity = one ? '0' : '1';
      if (!one) fillAll(n, col);
    });
  }
  if (skirtinner) {
    const skirt = param('skirtinner', 'paramCol');
    const adjusted = hexMinus(skirt, 30);
    const one = param('skirtinner', 'paramParts') === '1';
    const c1 = one ? '#955EA5' : skirt, c2 = one ? '#774B8A' : adjusted || skirt;
    [q('g.skirtcolor, path.skirtcolor'), q('g.skirtcolor2, path.skirtcolor2')].forEach((list, i) => {
      list.forEach((n) => {
        const tag = n.tagName.toLowerCase();
        if (tag === 'g') fillAll(n, i === 0 ? c1 : c2);
        else if (tag === 'path') (n as SVGElement).style.fill = i === 0 ? c1 : c2;
        (n as SVGElement).style.opacity = '1';
      });
    });
  }
}
// アイシャドウ（charat.legacy.js の updateEyeshadowVisibility と同じ。フィルターのレイヤーも隠す）
function eyeshadow() {
  const es = layerNode('eyeshadow'), parts = param('eyeshadow', 'paramParts'), col = es ? param('eyeshadow', 'paramCol') : '';
  const filter = layerNode('filter');
  if (filter) (filter as SVGElement).style.display = 'none';
  if (es) (es as SVGElement).style.display = 'none';
  C.svg.querySelectorAll('.eyeshadow1, .eyeshadow2, .eyeshadow3, .eyeshadow4').forEach((n) => {
    const el = n as SVGElement, cl = el.classList;
    const want = cl.contains('eyeshadow1') ? '2' : cl.contains('eyeshadow2') ? '3' : cl.contains('eyeshadow3') ? '4' : '5';
    el.style.display = parts === want ? 'block' : 'none';
    if (col) el.style.fill = col;
    else el.style.removeProperty('fill');
  });
}
// くちのうすさ（charat.legacy.js の updateLipOpacity と同じ）
function lipOpacity() {
  const mouth = layerNode('mouth');
  if (!mouth) return;
  const op = param('mouth', 'paramOpacity');
  if (!op) return;
  mouth.querySelectorAll('.lip').forEach((n) => n.setAttribute('opacity', op));
}
// キャラの縁の黒い線（charat.legacy.js の copyAndOutlineElements と同じ。#chaSvgMake の直下の g を複製して、bg2_f の直後に）
const OUTLINE_SKIP = ['lcart_chara', 'lcart_bg2_r', 'lcart_ef', 'lcart_bg2_f', 'lcart_eyeshadow', 'lcart_inner'];
function outline() {
  const make = C.svg.querySelector('#chaSvgMake');
  const after = layerNode('bg2_f');
  if (!make || !after || !after.parentNode) return;
  const old = C.svg.querySelector('#copiedElementsContainer');
  if (old && old.parentNode) old.parentNode.removeChild(old);
  const box = document.createElementNS(SVGNS, 'g');
  box.id = 'copiedElementsContainer';
  Array.from(make.children).forEach((g, index) => {
    if (g.tagName !== 'g' || OUTLINE_SKIP.indexOf(g.id) >= 0) return;
    const copy = g.cloneNode(true) as SVGElement;
    copy.setAttribute('stroke', 'black');
    copy.setAttribute('stroke-width', '1');
    copy.id = 'copiedElement_' + index;
    box.appendChild(copy);
  });
  if (box.children.length > 0) after.parentNode.insertBefore(box, after.nextSibling);
}

// ---- KAWAII2（2026-10-28〜） ----
// 色のフィルター（charat.legacy.js の filterCheck・applyFilter と同じ番号と行列）。filter のカテゴリのパーツの番号で、キャラ全体（g#chaSvgMake）に掛ける。
// 定義もクラシック版と同じく #chaSvgMake の中に置くので、完成の画像・高画質DL・セーブの絵（どれもキャラの SVG の写し）にも一緒に入る。
// 1 はなし。パーツが無い・ほかの番号のときは、クラシック版は前のフィルターのままにするが、こちらは外す（同じ保存データなら同じ絵に）
const COLOR_FILTERS: Record<string, [string, string]> = {
  '2': ['sepia', '0.393 0.769 0.189 0 0 0.349 0.686 0.168 0 0 0.272 0.534 0.131 0 0 0 0 0 1 0'],
  '3': ['monochrome', '0.33 0.33 0.33 0 0 0.33 0.33 0.33 0 0 0.33 0.33 0.33 0 0 0 0 0 1 0'],
  '4': ['coolBlue', '0.6 0.2 0.2 0 0 0.2 0.7 0.2 0 0 0.2 0.2 0.7 0 0 0 0 0 1 0'],
  '5': ['warmSun', '0.9 0.2 0.1 0 0.05 0.15 0.85 0.2 0 0.05 0.1 0.15 0.7 0 0.05 0 0 0 1 0'],
  '6': ['vintage', '0.85 0.45 0.15 0 0.05 0.25 0.85 0.35 0 0.05 0.15 0.25 0.6 0 0.05 0 0 0 1 0'],
  '7': ['softPink', '0.95 0.35 0.45 0 0.05 0.1 0.9 0.15 0 0.05 0.1 0.2 0.95 0 0.05 0 0 0 1 0'],
  '8': ['dreamy', '1.05 0.2 0.3 0 0.05 0.1 0.9 0.3 0 0.05 0.1 0.2 1.1 0 0 0 0 0 1 0'],
  '9': ['retro', '0.9 0.45 0.05 0 0.05 0.4 0.85 0.15 0 0.05 0.2 0.25 0.65 0 0.05 0 0 0 1 0'],
  '10': ['future', '1.1 0.2 0.2 0 0 0.1 0.7 0.3 0 0 0.2 0.2 1.2 0 0 0 0 0 1 0'],
};
function colorFilter() {
  const make = C.svg.querySelector('#chaSvgMake') as SVGGElement | null;
  if (!make) return;
  // フィルターのカテゴリのパーツ（番号の絵）はキャラには出さない（クラシック版はアイシャドウの決まりが隠す）
  const layer = layerNode('filter');
  if (layer) (layer as SVGElement).style.display = 'none';
  const f = COLOR_FILTERS[param('filter', 'paramParts')];
  let defs = make.querySelector(':scope > defs.look-colorfilter');
  if (!f) {
    make.style.removeProperty('filter');
    if (defs) defs.remove();
    return;
  }
  if (!defs) {
    defs = document.createElementNS(SVGNS, 'defs');
    defs.setAttribute('class', 'look-colorfilter');
    make.appendChild(defs);
  }
  if (!defs.querySelector('filter[id="' + f[0] + '"]')) {
    const filter = document.createElementNS(SVGNS, 'filter');
    filter.setAttribute('id', f[0]);
    const m = document.createElementNS(SVGNS, 'feColorMatrix');
    m.setAttribute('type', 'matrix');
    m.setAttribute('values', f[1]);
    filter.appendChild(m);
    defs.textContent = '';
    defs.appendChild(filter);
  }
  make.style.filter = 'url(#' + f[0] + ')';
}

// ---- KAWAII（2026-10-29〜） ----
// 手の見え方（charat.legacy.js の updateHandVisibility の見え方の分と同じ。パーツ番号の書きかえは core/ops.ts の exclusive）。
// 両手が付いていれば左手・右手を隠して両手を出し、左手・右手のどちらかが付いていれば両手を隠して付いているほうだけ出す（はじめに読んだ番号で両方を順に＝両方付いていれば左右が勝つ）。
// 手を上げていないとき（両手が無いか 100 番より前の番号でなく、左手も無い）は袖（cloth_y）を隠す。番号はクラシック版と同じく文字のまま比べる（"2" >= "100" も真）
function excHands() {
  // パーツの無い手（保存データに行が無い）は「なし」と同じ（クラシック版は読み込みで行ごとに排他を掛け直すあいだに、行の無い手の番号も 1 になる）
  const hand = param('hand_r', 'paramParts') || '1', handl = param('handl_r', 'paramParts') || '1', handr = param('handr', 'paramParts') || '1';
  const show = (name: string, on: boolean) => { const g = layerNode(name); if (g) (g as SVGElement).style.display = on ? 'block' : 'none'; };
  if (hand !== '1') {
    ['handl_r', 'handl_f', 'handr'].forEach((n) => show(n, false));
    ['hand_r', 'hand_f'].forEach((n) => show(n, true));
  }
  if (handl !== '1' || handr !== '1') {
    ['hand_r', 'hand_f'].forEach((n) => show(n, false));
    ['handl_r', 'handl_f'].forEach((n) => show(n, handl !== '1'));
    show('handr', handr !== '1');
  }
  show('cloth_y', !((hand === '1' || hand >= '100') && handl === '1'));
  // 左の手を上げているときだけのもの（look.handsUp。KAWAII のブレスレットは向かって左の手首に描いてあり、手が無い・右手だけ・物を持つ両手では宙に浮く。ユーザー 2026-10-29）。
  // 隠すだけで、選んだものは覚えている（手を上げるとまた出る）
  (LOOK!.handsUp || []).forEach((key) => {
    const c = C.cats.filter((x) => keyOf(x) === key)[0];
    if (c) c.LAYER.forEach((l) => { const g = layerNode(l); if (g) (g as SVGElement).style.display = handsUp() ? '' : 'none'; });
  });
}
// 向かって左の手を上げているか: 手（左）が付いている、または両手が 100 番より前（げんこつ・がおー・肉球。100 番から後ろは物を持つ形で手首の位置が違う）。
// 袖（cloth_y）の決まりと同じ考えだが、クラシック版の袖は番号を文字のまま比べていて "2" >= "100" が真になるので、ここは数で比べる
export function handsUp(): boolean {
  const hand = param('hand_r', 'paramParts') || '1', handl = param('handl_r', 'paramParts') || '1';
  return handl !== '1' || (hand !== '1' && Number(hand) < 100);
}
// 一覧の上のひとこと（文言のキー。無ければ null）: 左の手を上げていないときのブレスレット
export function gridHint(cat: Category): string | null {
  if (!LOOK || !LOOK.excHands || !LOOK.handsUp || LOOK.handsUp.indexOf(keyOf(cat)) < 0) return null;
  return handsUp() ? null : 'hint_handsup';
}

// ぼかしと乗算の filter（charat.legacy.js の addFilterblur と同じ中身）。キャラの SVG に 1 回だけ
function ensureFilters() {
  if (!C.svg || C.svg.querySelector('#filblur1')) return;
  const defs = document.createElementNS(SVGNS, 'defs');
  defs.setAttribute('class', 'look-filters');
  for (let i = 1; i <= 10; i++) {
    const f = document.createElementNS(SVGNS, 'filter');
    f.setAttribute('id', 'filblur' + i);
    const b = document.createElementNS(SVGNS, 'feGaussianBlur');
    b.setAttribute('stdDeviation', String(i));
    f.appendChild(b);
    defs.appendChild(f);
  }
  const m = document.createElementNS(SVGNS, 'filter');
  m.setAttribute('id', 'multiplyFilter');
  [['#sourceGraphic1', 'input1'], ['#sourceGraphic2', 'input2']].forEach(([href, result]) => {
    const im = document.createElementNS(SVGNS, 'feImage');
    im.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', href);
    im.setAttribute('result', result);
    m.appendChild(im);
  });
  const bl = document.createElementNS(SVGNS, 'feBlend');
  bl.setAttribute('mode', 'multiply');
  bl.setAttribute('in', 'input1');
  bl.setAttribute('in2', 'input2');
  bl.setAttribute('result', 'output');
  m.appendChild(bl);
  defs.appendChild(m);
  C.svg.insertBefore(defs, C.svg.firstChild);
}

// ---- パーツの一覧のサムネイル（2026-10-27〜、DRESSUP2。ユーザー依頼） ----
const SHADOW_SEL = '.eyeshadow1, .eyeshadow2, .eyeshadow3, .eyeshadow4';
// アイシャドウのパーツの番号（2〜5）→ 出す形（.eyeshadow1〜4）。1 は「なし」
function shadowWant(n: Element) {
  const cl = n.classList;
  return cl.contains('eyeshadow1') ? 2 : cl.contains('eyeshadow2') ? 3 : cl.contains('eyeshadow3') ? 4 : 5;
}
const SKIN_SEL = '.skincolor, .skincolor2, .skincolor3, .skincolor4, .skincolor5';
// 肌の色（r,g,b の文字の集まり）: 肌の元になるレイヤー（skinBase）のカテゴリと同じグループ（肌の色の連動）のカテゴリの、元の色といまの色
function skinFills(): Record<string, true> {
  const out: Record<string, true> = {};
  const add = (col: string | null | undefined) => { const v = col ? rgbOf(col) : null; if (v) out[v.map(Math.round).join(',')] = true; };
  const base = LOOK && LOOK.skinBase ? catOfLayer(LOOK.skinBase) : null;
  if (!base || !base.GROUP) return out;
  C.cats.forEach((c) => { if (c.GROUP !== base.GROUP) return; add(c.COLOR[0]); c.LAYER.forEach((l) => add(layerColor(l))); });
  return out;
}
function hideSkin(key: string, g: Element) {
  if (!LOOK) return;
  // thumbSkinBgLayers（GENESIS・BIGBANG の目の形）: はみ出した瞳を隠す肌色の形が class の無いレイヤーまるごと（eyeform_f）なので、レイヤー（サムネイルの写しの data-layer）で見分けて地の色で塗る（ユーザー 2026-10-28）
  (LOOK.thumbSkinBgLayers || []).forEach((l) => { g.querySelectorAll('[data-layer="' + l + '"]').forEach((n) => n.classList.add('thumb-skin-bg')); });
  // thumbSkinBgFill（GENESIS2 の め）: 肌色の形が まつげの線と同じレイヤーに混ざっているので、塗りの色が肌の色のものだけを地の色で塗る
  if (LOOK.thumbSkinBgFill && LOOK.thumbSkinBgFill.indexOf(key) >= 0) {
    const skins = skinFills();
    g.querySelectorAll('path,polygon,ellipse,circle,rect').forEach((n) => {
      const c = rgbOf((n as SVGElement).style.fill || n.getAttribute('fill') || '');
      if (c && skins[c.map(Math.round).join(',')]) n.classList.add('thumb-skin-bg');
    });
  }
  // thumbSkinBg（KAWAII の め）: 肌色の形は外さずに、サムネイルの地の色（style.css の --part-bg）で塗る。はみ出した瞳を隠す形なので、外すと瞳がはみ出して見える（ユーザー 2026-10-29）
  if (LOOK.thumbSkinBg && LOOK.thumbSkinBg.indexOf(key) >= 0) { g.querySelectorAll(SKIN_SEL).forEach((n) => n.classList.add('thumb-skin-bg')); return; }
  if (!LOOK.thumbHideSkin || LOOK.thumbHideSkin.indexOf(key) < 0) return;
  g.querySelectorAll(SKIN_SEL).forEach((n) => { (n as SVGElement).style.display = 'none'; });
}
// サムネイルの手入れ: アイシャドウの形を外す（キャラではアイシャドウのカテゴリで選んだ1つだけが出る）。
// thumbHideSkin のカテゴリは、はみ出しを隠すために重ねた肌色の形も外す（DRESSUP2 の め。ユーザー依頼 2026-10-27）
// thumbSkinBg のカテゴリは、その形をサムネイルの地の色で塗る（KAWAII の め。外すとはみ出した瞳が見える）
export function thumbClean(cat: Category, g: Element) {
  if (!LOOK) return;
  if (LOOK.eyeshadow) g.querySelectorAll(SHADOW_SEL).forEach((n) => { (n as SVGElement).style.display = 'none'; });
  hideSkin(keyOf(cat), g);
}
// いまのキャラから作るサムネイルか（作り直しが要る。範囲も覚えない）。番号の絵・ON/OFF の絵のカテゴリ（ユーザー 2026-10-27・28）:
//   eyeshadow   いまのめ（look.eyeshadowThumb のカテゴリ）にその番号のアイシャドウ。1 は「なし」
//   inner       いまの髪のインナーの所（.innercolor）にいまの色（look.innerThumb）。1 は「なし」
//   skirtinner  いまのスカートの裏（.skirtcolor）に、1 は元の色（むらさき）・2 はいまの色（look.innerThumb）
//   bow         いまの服の胸元（.bowtie）にいまの色のリボン（look.innerThumb。1 は「なし」。KAWAII。いまの服にリボンの所が無ければ ON/OFF の絵のまま）
//   filter      いまのキャラにその番号の色のフィルター（look.colorFilter。1 はフィルターなしのキャラ。KAWAII2、クラシック版は ①〜⑨ の番号の絵）
//   onBase      ハイライト・メイクなど（look.onBase の cats）: いまのキャラの土台（base のカテゴリ。め・かお）の写しに、その番号のパーツを重ねる（位置・色はいまの状態。中身の無いパーツは「なし」。
//               寄せ方は土台の範囲で（frame があればそのレイヤーだけ）。KAWAII、ユーザー 2026-10-29「ハイライトは選んでいる目の上に」「メイクは顔の輪郭を下に」）
export function liveThumb(cat: Category | undefined) {
  if (!cat || !LOOK) return false;
  const k = keyOf(cat);
  if (k === 'eyeshadow') return !!LOOK.eyeshadow && !!(LOOK.eyeshadowThumb && LOOK.eyeshadowThumb.length);
  if (k === 'inner' || k === 'skirtinner' || k === 'bow') return !!LOOK.adjustColors && !!LOOK.innerThumb;
  if (k === 'filter') return !!LOOK.colorFilter;
  if (baseOf(k)) return true;
  return false;
}
// 色のフィルターのサムネイルの元（いまのキャラの #chaSvgMake の中身の写し。フィルターは写さない）を、見えない svg の defs に 1 つだけ置き、
// サムネイルはそれを <use> で指して自分のフィルターを掛ける（キャラを 10 回写さない）。キャラを描き直したら（drawn・drawnAll）次に使うときに作り直す
let filterBaseVer = 0, filterBaseBuilt = -1;
function filterBase(): string {
  const id = 'lookFilterBase';
  if (filterBaseBuilt === filterBaseVer && document.getElementById(id)) return id;
  let holder: Element | null = document.getElementById('lookFilterHolder');
  if (!holder) {
    const svg = document.createElementNS(SVGNS, 'svg');
    svg.id = 'lookFilterHolder';
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('width', '0');
    svg.setAttribute('height', '0');
    svg.setAttribute('style', 'position:absolute;width:0;height:0;overflow:hidden;pointer-events:none');
    svg.appendChild(document.createElementNS(SVGNS, 'defs'));
    document.body.appendChild(svg);
    holder = svg;
  }
  const defs = holder.firstChild as Element;
  defs.textContent = '';
  const g = copyLayers(() => true);
  g.id = id;
  defs.appendChild(g);
  filterBaseBuilt = filterBaseVer;
  return id;
}
function filterThumb(part: Part): SVGGElement {
  const g = document.createElementNS(SVGNS, 'g');
  const use = document.createElementNS(SVGNS, 'use');
  use.setAttribute('href', '#' + filterBase());
  const f = COLOR_FILTERS[String(part.id)];
  if (f) {
    const fid = 'lookThumbFilter' + part.id;
    const defs = document.createElementNS(SVGNS, 'defs');
    const filter = document.createElementNS(SVGNS, 'filter');
    filter.setAttribute('id', fid);
    const m = document.createElementNS(SVGNS, 'feColorMatrix');
    m.setAttribute('type', 'matrix');
    m.setAttribute('values', f[1]);
    filter.appendChild(m);
    defs.appendChild(filter);
    g.appendChild(defs);
    use.setAttribute('filter', 'url(#' + fid + ')');
  }
  g.appendChild(use);
  return g;
}
// パーツの範囲のまん中（拡大を掛けたあと。見えない所に置いた svg の中で計る。charatDraw.rotateCenter と同じく getBBox の範囲）
let measureSvg: SVGSVGElement | null = null;
function centerOf(node: Element): { x: number; y: number } | null {
  if (!measureSvg) {
    measureSvg = document.createElementNS(SVGNS, 'svg') as SVGSVGElement;
    measureSvg.setAttribute('aria-hidden', 'true');
    measureSvg.setAttribute('width', '0');
    measureSvg.setAttribute('height', '0');
    measureSvg.setAttribute('style', 'position:absolute;width:0;height:0;overflow:hidden;pointer-events:none');
    document.body.appendChild(measureSvg);
  }
  const w = document.createElementNS(SVGNS, 'g');
  w.appendChild(node.cloneNode(true));
  measureSvg.appendChild(w);
  let b: DOMRect | null = null;
  try { b = w.getBBox(); } catch (e) {}
  w.remove();
  return b && (b.width || b.height) ? { x: b.x + b.width / 2, y: b.y + b.height / 2 } : null;
}
// 土台に重ねるサムネイル（look.onBase。ハイライトは め、メイクは かお の上）: キャラのレイヤーを DOM の順に見て、土台のレイヤーは写し、そのカテゴリのレイヤー（左右とも）は
// いまのレイヤーの g の位置（transform）を写した入れ物に、その番号のパーツを入れていまの色で塗る（render.ts と同じ charatDraw.colorLine。拡大も同じ）。
// 重なりはキャラと同じ（め の奥 → ハイライト → め の手前）。はみ出しを隠す肌色の形は thumbSkinBg / thumbHideSkin のとおりに
type OnBase = { cats: string[]; base: string[]; frame?: string[]; fitPart?: boolean; near?: number; side?: 'left' | 'right'; box?: number[]; fixed?: Record<string, number>; sideNear?: number };
function baseOf(key: string): OnBase | null {
  return (LOOK && LOOK.onBase ? LOOK.onBase.filter((o) => o.cats.indexOf(key) >= 0)[0] : null) || null;
}
function onBaseThumb(cat: Category, part: Part, o: OnBase): SVGGElement {
  const eyeLayers: Record<string, true> = {}, litLayer: Record<string, { cat: Category; i: number }> = {};
  // その番号で描くのは、このカテゴリと左右でひと組の相手だけ（同じ土台にまとめたほかのカテゴリ＝メイクとまゆ などは描かない）
  const k0 = keyOf(cat), pairs: Record<string, string> = CONFIG.pairs || {};
  // 「左」「右」のときは、その側のパーツと、土台もその側だけ（左のハイライトは左の め の上。設定 pairThumbSide）
  const side = pairThumbSide(k0), base = sideKeys(o.base, side);
  // 「左」「右」のとき、土台が両側で1つのカテゴリ（GENESIS2 の め）なら、その側のパーツのまわりに寄せる（sideNear。片方だけ描くパーツの範囲＝その側）
  const near = o.near || (side && o.sideNear) || 0;
  const draw = side ? [k0] : [k0].concat(pairs[k0] ? [pairs[k0]] : [], Object.keys(pairs).filter((l) => pairs[l] === k0));
  base.forEach((key) => { const c = C.cats.filter((x) => keyOf(x) === key)[0]; if (c) c.LAYER.forEach((l) => { eyeLayers['lcart_' + l] = true; }); });
  draw.forEach((key) => { const c = C.cats.filter((x) => keyOf(x) === key)[0]; if (c) c.LAYER.forEach((l, i) => { litLayer['lcart_' + l] = { cat: c, i: i }; }); });
  // 決まった番号のパーツを土台にするカテゴリ（fixed。GENESIS2 は腕が アウター のパーツの中にあり、アウター 1 が素肌の腕。いまのアウターだとタトゥーが隠れる。ユーザー 2026-10-29）
  const fixedLayer: Record<string, { cat: Category; i: number; id: number }> = {};
  Object.keys(o.fixed || {}).forEach((key) => { const c = C.cats.filter((x) => keyOf(x) === key)[0]; if (c) c.LAYER.forEach((l, i) => { fixedLayer['lcart_' + l] = { cat: c, i: i, id: o.fixed![key] }; }); });
  const g = document.createElementNS(SVGNS, 'g');
  C.svg.querySelectorAll('g[id^="lcart_"]').forEach((layer) => {
    const fx = fixedLayer[layer.id];
    if (fx) {
      const fp = findPart(fx.cat, fx.id), src = fp ? layersOf(fx.cat, fp)[fx.i] : null;
      if (!src) return;
      const n = src.cloneNode(true) as Element;
      n.setAttribute('data-layer', layer.id.slice(6));
      if ((o.frame && o.frame.indexOf(layer.id.slice(6)) < 0) || near || o.side) n.setAttribute('data-nobox', '');
      g.appendChild(n);
      return;
    }
    if (eyeLayers[layer.id]) {
      if ((layer as SVGElement).style.display === 'none') return;
      const copy = layer.cloneNode(true) as Element;
      [copy].concat(Array.prototype.slice.call(copy.querySelectorAll('[id]'))).forEach((n: Element) => n.removeAttribute('id'));
      copy.setAttribute('data-layer', layer.id.slice(6)); // どのレイヤーの写しか（thumbSkinBgLayers）
      // 寄せ方は frame のレイヤーだけで（かお の首から下は入れない）。パーツのまわりに寄せるもの（near・side）は土台を入れない
      if ((o.frame && o.frame.indexOf(layer.id.slice(6)) < 0) || near || o.side) copy.setAttribute('data-nobox', '');
      g.appendChild(copy);
      return;
    }
    const lit = litLayer[layer.id];
    if (!lit) return;
    const p = findPart(lit.cat, part.id), src = p ? layersOf(lit.cat, p)[lit.i] : null;
    if (!src) return;
    const box = document.createElementNS(SVGNS, 'g');
    box.setAttribute('data-layer', layer.id.slice(6));
    // 位置だけ写す（うすさは掛けない＝ほかのカテゴリの一覧と同じくはっきり。テンプレートのメイクはうすさ 50% で淡く見分けにくかった）
    { const v = layer.getAttribute('transform'); if (v != null) box.setAttribute('transform', v); }
    if (!o.fitPart && !near && !o.side) box.setAttribute('data-nobox', ''); // サムネイルの寄せ方は土台の範囲だけで（目の外まで広がるハイライトでも同じ大きさに。parts.ts の thumbBox）。fitPart はパーツの範囲も入れる（顔の外にぶら下がるイヤリング）
    const node = src.cloneNode(true) as Element;
    const st = C.state[keyOf(lit.cat)];
    const zoom = st && lit.cat.IS_ZOOM != 0 ? Number(st.scale) || 1 : 1;
    if (zoom !== 1) node.setAttribute('transform', charatDraw.zoomTransform(zoom, 500, 500));
    // 回す中心は、その番号のパーツの範囲のまん中で計り直す（キャラで選んだときの render.ts の placeOf と同じ。キャラのレイヤーの中心はいまのパーツのもので、
    // 「なし」のときは空の範囲で (0,0) になり、一覧の絵がずれていた。ユーザー 2026-10-29）
    const tf = box.getAttribute('transform'), rm = tf ? /,?\s*rotate\(\s*([^,\s)]+)[^)]*\)/.exec(tf) : null;
    if (tf && rm) {
      const c = centerOf(node);
      box.setAttribute('transform', tf.replace(rm[0], c ? ',rotate(' + rm[1] + ',' + c.x + ',' + c.y + ')' : ''));
    }
    box.appendChild(node);
    if (st) {
      const layers = lit.cat.LAYER.map((_, j) => (j === lit.i ? box : null));
      charatDraw.colorLine(layers, lit.cat, st);
    }
    g.appendChild(box);
  });
  base.forEach((key) => hideSkin(key, g));
  // パーツのまわりに寄せる（parts.ts の thumbBox が読む）
  if (near) g.setAttribute('data-near', String(near));
  if (o.box && o.box.length === 4) g.setAttribute('data-box', o.box.join(',')); // どの番号も同じ切り取り（イヤリングの右の耳のアップ）
  if (o.side) g.setAttribute('data-side', o.side);
  if (side && !o.box && !near) g.setAttribute('data-pad', '0.12'); // 片目だけのときは まつげの先が切れないように少し余白（parts.ts の thumbBox）
  return g;
}
// 「なし」のタイル（ほかのカテゴリの「なし」と同じ）にする番号
export function liveNone(cat: Category, part: Part) {
  if (!liveThumb(cat)) return false;
  const k = keyOf(cat), id = Number(part.id);
  if (baseOf(k)) return false; // 土台に重ねる一覧は、中身の無いパーツだけが「なし」（parts.ts の thumbnail が partIsEmpty で。まゆ は 0000 が空で 0001 はまゆ。ユーザーの指摘 2026-10-29）
  return k === 'eyeshadow' ? id < 2 : k === 'inner' || k === 'bow' ? id === 1 : false;
}
// キャラのレイヤーを DOM の順に写す（見えているものだけ。写しの id は外す）
function copyLayers(take: (layer: Element) => boolean): SVGGElement {
  const g = document.createElementNS(SVGNS, 'g');
  C.svg.querySelectorAll('g[id^="lcart_"]').forEach((layer) => {
    if ((layer as SVGElement).style.display === 'none' || !take(layer)) return;
    const copy = layer.cloneNode(true) as Element;
    [copy].concat(Array.prototype.slice.call(copy.querySelectorAll('[id]'))).forEach((n: Element) => n.removeAttribute('id'));
    g.appendChild(copy);
  });
  return g;
}
// 印（class）のあるレイヤーと、同じカテゴリのほかのレイヤー（スカートの前と後ろなど）
function layersWith(sel: string) {
  const names: Record<string, true> = {};
  C.svg.querySelectorAll('g[id^="lcart_"]').forEach((layer) => {
    if (!layer.querySelector(sel)) return;
    const cat = catOfLayer(layer.id.slice(6));
    if (cat) cat.LAYER.forEach((l) => { names['lcart_' + l] = true; }); else names[layer.id] = true;
  });
  return (layer: Element) => !!names[layer.id];
}
export function liveThumbOf(cat: Category, part: Part): SVGGElement | null {
  if (!LOOK || !C.svg) return null;
  const k = keyOf(cat), id = Number(part.id);
  if (k === 'filter') return filterThumb(part);
  const ob = baseOf(k);
  if (ob) return onBaseThumb(cat, part, ob);
  if (k === 'eyeshadow') {
    // いまのキャラの め・ハイライトのレイヤーを写して、その番号のアイシャドウだけを出す（色はキャラと同じ決まり）
    const names: Record<string, true> = {};
    (LOOK.eyeshadowThumb || []).forEach((key) => { const c = C.cats.filter((x) => keyOf(x) === key)[0]; if (c) c.LAYER.forEach((l) => { names['lcart_' + l] = true; }); });
    const col = layerNode('eyeshadow') ? param('eyeshadow', 'paramCol') : '';
    const g = copyLayers((layer) => !!names[layer.id]);
    g.querySelectorAll(SHADOW_SEL).forEach((n) => {
      const el = n as SVGElement;
      el.style.display = shadowWant(n) === id ? 'block' : 'none';
      if (col) el.style.fill = col; else el.style.removeProperty('fill');
    });
    hideSkin('eyeshadow', g);
    return g;
  }
  const st = C.state[k], col = (st && st.col) || cat.COLOR[0] || '';
  if (k === 'inner') {
    // キャラと同じ決まり（adjustColors）で、インナーの所を出していまの色に
    const g = copyLayers(layersWith('g.innercolor'));
    g.querySelectorAll('g.innercolor').forEach((n) => { (n as SVGElement).style.opacity = '1'; fillAll(n, col); });
    return g;
  }
  if (k === 'bow') {
    // キャラと同じ決まり（adjustColors）で、服の .bowtie を出していまの色に
    if (!C.svg.querySelector('g[id="lcart_cloth_f"] .bowtie')) return null;
    const g = copyLayers(layersWith('.bowtie'));
    const c2 = hexMinus(col, 30);
    g.querySelectorAll('.bowtie').forEach((n) => { (n as SVGElement).style.display = ''; });
    g.querySelectorAll('.bowcolor').forEach((n) => {
      const tag = n.tagName.toLowerCase();
      if (tag === 'path' || tag === 'g') (n as SVGElement).style.fill = col;
      fillAll(n, col);
    });
    if (c2) g.querySelectorAll('.bowcolor2').forEach((n) => { if (n.tagName.toLowerCase() === 'path') (n as SVGElement).style.fill = c2; fillAll(n, c2); });
    return g;
  }
  if (k === 'skirtinner') {
    const one = id === 1;
    const c1 = one ? '#955EA5' : col, c2 = one ? '#774B8A' : hexMinus(col, 30) || col;
    const g = copyLayers(layersWith('.skirtcolor, .skirtcolor2'));
    [g.querySelectorAll('g.skirtcolor, path.skirtcolor'), g.querySelectorAll('g.skirtcolor2, path.skirtcolor2')].forEach((list, i) => {
      list.forEach((n) => {
        const tag = n.tagName.toLowerCase();
        if (tag === 'g') fillAll(n, i === 0 ? c1 : c2);
        else if (tag === 'path') (n as SVGElement).style.fill = i === 0 ? c1 : c2;
        (n as SVGElement).style.opacity = '1';
      });
    });
    return g;
  }
  return null;
}

// クラシック版の順番（肌 → グラデーションの止め → 線。charat.legacy.js の afterChangeParts・afterChangeColor）
export function applyLook() {
  if (!LOOK || !C.svg) return;
  if (LOOK.blurFilters) ensureFilters();
  if (LOOK.skinBase) skinLink(LOOK.skinBase);
  if (LOOK.adjustColors) adjustColors();
  (LOOK.gradStops || []).forEach(gradStops);
  if (LOOK.eyeshadow) eyeshadow();
  if (LOOK.lipOpacity) lipOpacity();
  if (LOOK.outline) outline();
  if (LOOK.lineColor && LOOK.lineColor.length) lineColor(LOOK.lineColor);
  if (LOOK.colorFilter) colorFilter();
  if (LOOK.excHands) excHands();
}
// 全部を描き直したあと（読み込み・ランダム）: 回転しているカテゴリを、決まりで隠したあとの範囲で回し直す（クラシック版の中身の core/classic.ts の replaceRotated と同じ。
// 回す中心はレイヤーの範囲のまん中なので、アイシャドウの形（.eyeshadow1〜4）を隠す前と後で変わる。KAWAII のテンプレートの め は 1 度回してあり、ずれていた。2026-10-29）
function replaceRotated() {
  if (!LOOK || !LOOK.eyeshadow || !C.svg) return;
  C.cats.forEach((cat) => {
    const st = C.state[keyOf(cat)];
    if (!st || st.parts == null || !(Number(st.rotate) || 0) || !findPart(cat, st.parts)) return;
    place(cat, st, 'load');
  });
}
// 移動・拡大・回転・反転のあと（クラシック版の afterChangeTransform）: 縁の黒い線だけ作り直す。引っぱっている間は止まってから 1 回
let outlineTimer = 0;
function afterPlace(why: string) {
  if (!LOOK || !LOOK.outline || !C.svg) return;
  clearTimeout(outlineTimer);
  if (why === 'drag') outlineTimer = window.setTimeout(outline, 120);
  else outline();
}

if (LOOK) {
  // 全部を描き直す途中（読み込み・ランダム・元に戻す）は、最後の drawnAll で 1 回だけ
  const BULK: Record<string, true> = { load: true, random: true, undo: true };
  on('drawn', (_cat, _st, why) => { filterBaseVer++; if (!BULK[why]) applyLook(); });
  on('drawnAll', () => { filterBaseVer++; applyLook(); replaceRotated(); });
  // place は描き直し（render）の中でも呼ばれるが、そのときは drawn のあとの applyLook が作り直す
  on('placed', (_cat, _st, why) => { if (why !== 'load' && why !== 'random' && why !== 'undo') afterPlace(why); });
}
